import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, CheckCircle2, Loader2, ClipboardList } from "lucide-react";
import { SERVICES, CONTACT, PROPERTY_TYPES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", phone: "", email: "", address: "", property_type: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleService = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      toast.error("Please select at least one service for your quote.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        property_type: form.property_type || undefined,
        address: form.address || undefined,
        message: form.message || undefined,
        services: selected,
      });
      toast.success("Quote request sent! We'll be in touch within 24 hours.");
      setForm(INITIAL);
      setSelected([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#16283C] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F5A623] focus:border-transparent transition-shadow";

  return (
    <section id="quote" className="bg-[#0B131F] py-24" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 bg-[#F8FAFC] rounded-3xl p-7 sm:p-10">
            <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#D97706]">
              Free Estimate
            </span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#16283C] mt-3 uppercase">
              Build Your Quote
            </h2>
            <p className="mt-3 text-sm text-slate-600">
              Select every service you need — bundle jobs and save on mobilization.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#16283C] mb-3">
                  Services Needed *
                </label>
                <div className="flex flex-wrap gap-2.5">
                  {SERVICES.map((service) => {
                    const isActive = selected.includes(service.name);
                    return (
                      <button
                        type="button"
                        key={service.id}
                        data-testid="quote-form-service-checkbox"
                        onClick={() => toggleService(service.name)}
                        aria-pressed={isActive}
                        className={`flex items-center gap-2 text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-full border transition-[background-color,color,border-color,transform] duration-200 ${
                          isActive
                            ? "bg-[#F5A623] text-[#0C1827] border-[#F5A623] scale-[1.03]"
                            : "bg-white text-slate-600 border-[#E2E8F0] hover:border-[#F5A623]"
                        }`}
                      >
                        {isActive && <CheckCircle2 size={15} />}
                        {service.short}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    data-testid="quote-form-service-other"
                    onClick={() => toggleService("Other")}
                    aria-pressed={selected.includes("Other")}
                    className={`flex items-center gap-2 text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-full border transition-[background-color,color,border-color,transform] duration-200 ${
                      selected.includes("Other")
                        ? "bg-[#F5A623] text-[#0C1827] border-[#F5A623] scale-[1.03]"
                        : "bg-white text-slate-600 border-[#E2E8F0] hover:border-[#F5A623]"
                    }`}
                  >
                    {selected.includes("Other") && <CheckCircle2 size={15} />}
                    Other
                  </button>
                </div>
                {selected.includes("Other") && (
                  <p className="mt-3 text-xs text-slate-500" data-testid="quote-form-other-hint">
                    Chose Other? Just describe the work you need in the message box below.
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="quote-form-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Full Name *"
                  className={inputClass}
                />
                <input
                  data-testid="quote-form-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="Phone Number *"
                  className={inputClass}
                />
                <input
                  data-testid="quote-form-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Email Address *"
                  className={inputClass}
                />
                <input
                  data-testid="quote-form-address"
                  value={form.address}
                  onChange={update("address")}
                  placeholder="Property Address / Town"
                  className={inputClass}
                />
              </div>

              <div>
                <div className="flex flex-wrap gap-2.5">
                  {PROPERTY_TYPES.map((type) => (
                    <button
                      type="button"
                      key={type}
                      data-testid="quote-form-property-type"
                      onClick={() => setForm({ ...form, property_type: form.property_type === type ? "" : type })}
                      className={`text-xs font-semibold px-4 py-2 rounded-full border transition-[background-color,color,border-color] duration-200 ${
                        form.property_type === type
                          ? "bg-[#16283C] text-white border-[#16283C]"
                          : "bg-white text-slate-600 border-[#E2E8F0] hover:border-[#16283C]"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                data-testid="quote-form-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your project — lot size, terrain, timeline..."
                rows={4}
                className={`${inputClass} resize-none`}
              />

              <button
                data-testid="quote-form-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold text-base px-8 py-4 rounded-full hover:bg-[#E8961F] disabled:opacity-60 disabled:cursor-not-allowed transition-[background-color,opacity] duration-200 shadow-lg shadow-[#F5A623]/20"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending Request...
                  </>
                ) : (
                  "Send My Quote Request"
                )}
              </button>
            </form>
          </div>

          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-[#142234] border border-[#1E324A] rounded-3xl p-8" data-testid="quote-summary-panel">
              <div className="flex items-center gap-3 mb-5">
                <ClipboardList size={20} className="text-[#F5A623]" />
                <h3 className="font-display font-bold text-lg text-white">Your Quote Summary</h3>
              </div>
              {selected.length === 0 ? (
                <p className="text-sm text-slate-400">
                  No services selected yet — tap the services you need and they'll appear here.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {selected.map((s) => (
                    <li key={s} className="flex items-center gap-2.5 text-sm text-slate-200">
                      <CheckCircle2 size={16} className="text-[#F5A623] shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6 pt-5 border-t border-[#1E324A]">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Every request gets a personal callback — typically within 24 hours — from the
                  owner-operator who will run your job.
                </p>
              </div>
            </div>

            <div className="bg-[#F5A623] rounded-3xl p-8">
              <h3 className="font-display font-extrabold text-xl text-[#0C1827] uppercase">
                Prefer to Talk?
              </h3>
              <p className="mt-2 text-sm text-[#0C1827]/80">
                Call or email us directly — we answer.
              </p>
              <a
                href={CONTACT.phoneHref}
                data-testid="quote-call-link"
                className="mt-5 flex items-center gap-3 bg-[#0C1827] text-white rounded-xl px-5 py-4 hover:bg-[#16283C] transition-colors"
              >
                <Phone size={19} className="text-[#F5A623]" />
                <span className="font-display font-bold">{CONTACT.phone}</span>
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="quote-email-link"
                className="mt-3 flex items-center gap-3 bg-[#0C1827]/10 border border-[#0C1827]/20 text-[#0C1827] rounded-xl px-5 py-4 hover:bg-[#0C1827]/20 transition-colors"
              >
                <Mail size={19} />
                <span className="font-semibold text-sm break-all">{CONTACT.email}</span>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
